/**
 * AdminInvitesSection - Manage onboarding invites
 * List, send, edit, and revoke vendor/visitor invites
 * @atomic @idempotent
 */

import { memo, useState, useCallback, useEffect } from 'react'
import { vendorAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { TruckIcon, UserIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { toast } from '../../services/toastService'

const logger = createLogger('AdminInvitesSection')

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-600',
  revoked: 'bg-red-100 text-red-800'
}

const emptyForm = {
  email: '',
  track: 'vendor',
  entityId: '',
  message: '',
  expiresInDays: 7
}

const AdminInvitesSection = memo(function AdminInvitesSection() {
  const { execute } = useAsyncOperation()
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState(emptyForm)

  const loadInvites = useCallback(async () => {
    setLoading(true)
    await execute(() => vendorAPI.getInvites({ limit: 50 }), {
      onSuccess: (res) => setInvites(res.data?.invites ?? res.data ?? []),
      onError: (err) => logger.error('Failed to load invites', err)
    })
    setLoading(false)
  }, [execute])

  useEffect(() => { loadInvites() }, [loadInvites])

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    if (name === 'email') setEmailError('')
  }, [])

  const handleSend = useCallback(async (e) => {
    e.preventDefault()
    if (!form.email.trim()) { setEmailError('Email is required'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setEmailError('Invalid email'); return }
    setSubmitting(true)
    await execute(() => vendorAPI.sendInvite({
      email: form.email.trim(),
      track: form.track,
      entityId: form.entityId.trim() || undefined,
      message: form.message.trim() || undefined,
      expiresInDays: Number(form.expiresInDays) || 7
    }), {
      onSuccess: () => {
        toast.success('Invite sent')
        setForm(emptyForm)
        loadInvites()
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to send invite')
        logger.error('Send invite failed', err)
      }
    })
    setSubmitting(false)
  }, [form, execute, loadInvites])

  const handleRevoke = useCallback(async (inviteId) => {
    await execute(() => vendorAPI.revokeInvite(inviteId), {
      onSuccess: () => { toast.success('Invite revoked'); loadInvites() },
      onError: (err) => { toast.error('Failed to revoke'); logger.error('Revoke failed', err) }
    })
  }, [execute, loadInvites])

  const startEdit = useCallback((invite) => {
    setEditingId(invite.id)
    setEditForm({
      email: invite.email || '',
      track: invite.track || 'vendor',
      entityId: invite.entityId || '',
      message: invite.message || '',
      expiresInDays: invite.expiresAt ? Math.ceil((new Date(invite.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)) : 7
    })
  }, [])

  const handleEditChange = useCallback((e) => {
    const { name, value } = e.target
    setEditForm(prev => ({ ...prev, [name]: value }))
  }, [])

  const handleSaveEdit = useCallback(async (inviteId) => {
    if (!editForm.email.trim()) {
      toast.error('Email is required')
      return
    }
    await execute(() => vendorAPI.updateInvite(inviteId, {
      email: editForm.email.trim(),
      track: editForm.track,
      entityId: editForm.entityId.trim() || undefined,
      message: editForm.message.trim() || undefined,
      expiresInDays: Number(editForm.expiresInDays) || 7
    }), {
      onSuccess: () => {
        toast.success('Invite updated')
        setEditingId(null)
        loadInvites()
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to update')
        logger.error('Update failed', err)
      }
    })
  }, [editForm, execute, loadInvites])

  const cancelEdit = useCallback(() => {
    setEditingId(null)
    setEditForm(emptyForm)
  }, [])

  return (
    <div className="space-y-6">
      {/* Section heading */}
      <h3 className="text-base font-semibold text-gray-800">Invite Management</h3>

      {/* Send Invite */}
      <section className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Send Invite</h3>
        <form onSubmit={handleSend} className="space-y-2">
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <input
                id="inv-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Recipient email"
                className="w-full text-sm border border-gray-200 rounded px-3 py-2"
              />
              {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
            </div>
            <select
              id="inv-track"
              name="track"
              value={form.track}
              onChange={handleChange}
              className="text-sm border border-gray-200 rounded px-3 py-2"
            >
              <option value="vendor">Vendor</option>
              <option value="visitor">Visitor</option>
            </select>
          </div>

          {form.track === 'vendor' && (
            <input
              id="inv-entity"
              name="entityId"
              type="text"
              value={form.entityId}
              onChange={handleChange}
              placeholder="Entity ID to claim (optional)"
              className="w-full text-sm border border-gray-200 rounded px-3 py-2"
            />
          )}

          <div className="flex gap-2">
            <input
              id="inv-message"
              name="message"
              type="text"
              value={form.message}
              onChange={handleChange}
              placeholder="Personal message (optional)"
              className="flex-1 text-sm border border-gray-200 rounded px-3 py-2"
            />
            <input
              id="inv-expires"
              name="expiresInDays"
              type="number"
              min={1}
              max={30}
              value={form.expiresInDays}
              onChange={handleChange}
              title="Expires in days"
              className="w-20 text-sm border border-gray-200 rounded px-3 py-2"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
      </section>

      {/* Invite List */}
      <section className="space-y-2 border-t pt-4">
        <h3 className="text-sm font-medium text-gray-700">
          Invites <span className="text-gray-400 font-normal">({invites.length})</span>
        </h3>

        {loading ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : invites.length === 0 ? (
          <p className="text-xs text-gray-400">No invites yet.</p>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {invites.map(inv => (
              <div key={inv.id} className="bg-gray-50 rounded px-3 py-2">
                {editingId === inv.id ? (
                  // Edit mode
                  <div className="space-y-2">
                    <input
                      name="email"
                      type="email"
                      value={editForm.email}
                      onChange={handleEditChange}
                      className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                    />
                    <div className="flex gap-2">
                      <select
                        name="track"
                        value={editForm.track}
                        onChange={handleEditChange}
                        className="text-xs border border-gray-200 rounded px-2 py-1"
                      >
                        <option value="vendor">Vendor</option>
                        <option value="visitor">Visitor</option>
                      </select>
                      {editForm.track === 'vendor' && (
                        <input
                          name="entityId"
                          type="text"
                          value={editForm.entityId}
                          onChange={handleEditChange}
                          placeholder="Entity ID (optional)"
                          className="flex-1 text-xs border border-gray-200 rounded px-2 py-1"
                        />
                      )}
                    </div>
                    <input
                      name="message"
                      type="text"
                      value={editForm.message}
                      onChange={handleEditChange}
                      placeholder="Message (optional)"
                      className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        name="expiresInDays"
                        type="number"
                        min={1}
                        max={30}
                        value={editForm.expiresInDays}
                        onChange={handleEditChange}
                        className="w-16 text-xs border border-gray-200 rounded px-2 py-1"
                      />
                      <span className="text-xs text-gray-500">days</span>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(inv.id)}
                        className="ml-auto px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <div className="flex items-center justify-between text-xs">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-700 truncate block">{inv.email}</span>
                      <span className="text-gray-400 flex items-center gap-1.5 mt-0.5">
                        {inv.track === 'vendor'
                          ? <TruckIcon className="h-3 w-3 text-purple-400 flex-shrink-0" />
                          : <UserIcon className="h-3 w-3 text-blue-400 flex-shrink-0" />}
                        {inv.track}
                        {inv.entityName && <> · <span className="truncate">{inv.entityName}</span></>}
                        {' · '}
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[inv.status] ?? STATUS_COLORS.pending}`}>
                          {inv.status}
                        </span>
                        {inv.expiresAt && <> · {new Date(inv.expiresAt).toLocaleDateString()}</>}
                      </span>
                    </div>
                    {inv.status === 'pending' && (
                      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => startEdit(inv)}
                          className="text-gray-400 hover:text-primary"
                          title="Edit invite"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRevoke(inv.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Revoke invite"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
})

export default AdminInvitesSection
